import { type Story, StoryVisibility } from '@/@types/story'
import PageLoading from '@/components/PageLoading'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import StoryCommentsDrawer from '../comments/StoryCommentsDrawer'
import PersonalStoriesStage from './PersonalStoriesStage'
import useActiveStoryController from './useActiveStoryController'
import useStoryReadReceipt from './useStoryReadReceipt'
import styles from './PersonalStories.module.css'

interface PersonNavigation {
	canPrevious: boolean
	canNext: boolean
	onChange: (direction: 'PREVIOUS' | 'NEXT') => void
}

export interface StorySequenceProps {
	active: boolean
	stories: Story[]
	initialStoryId?: string
	hasNextPage?: boolean
	showStoryNavigation?: boolean
	personNavigation?: PersonNavigation
	playbackTimes: Map<string, number>
	readStories: Set<string>
	isMuted: boolean
	onMutedChange: (muted: boolean) => void
	onClose: () => void
	onStoryChange?: (story: Story) => void
	onLoadMore?: () => Promise<boolean>
}

const noMoreStories = () => Promise.resolve(false)

export default function StorySequence({
	active,
	stories,
	initialStoryId,
	hasNextPage = false,
	showStoryNavigation = true,
	personNavigation,
	playbackTimes,
	readStories,
	isMuted,
	onMutedChange,
	onClose,
	onStoryChange,
	onLoadMore = noMoreStories,
}: StorySequenceProps) {
	const [activeStoryId, setActiveStoryId] = useState(initialStoryId)
	const activeStory = stories.find((story) => story.id === activeStoryId) ?? stories[0]
	const activeStoryIndex = Math.max(
		0,
		stories.findIndex((story) => story.id === activeStory.id)
	)
	const interactions = useActiveStoryController({
		story: active ? activeStory : undefined,
		initialPlaybackTime: playbackTimes.get(activeStory.id),
		isMuted,
		onMutedChange,
		onPlaybackTimeChange: (time) => playbackTimes.set(activeStory.id, time),
	})

	const handleStoryChange = (storyIndex: number) => {
		const story = stories[storyIndex]
		if (!story) return

		setActiveStoryId(story.id)
		onStoryChange?.(story)
	}

	useStoryReadReceipt({ active, story: activeStory, readStories })

	return (
		<>
			<PersonalStoriesStage
				active={active}
				canPreviousPerson={personNavigation?.canPrevious ?? false}
				canNextPerson={personNavigation?.canNext ?? false}
				stories={stories}
				activeStory={activeStory}
				activeStoryIndex={activeStoryIndex}
				media={interactions.media}
				hasNextPage={hasNextPage}
				interactionDisabled={interactions.commentsOpened}
				showStoryNavigation={showStoryNavigation}
				onStoryChange={handleStoryChange}
				onLoadMore={onLoadMore}
				onClose={onClose}
				onPreviousPerson={() => personNavigation?.onChange('PREVIOUS')}
				onNextPerson={() => personNavigation?.onChange('NEXT')}
				onDoubleClickLike={interactions.handleDoubleClickLike}
				onLike={interactions.handleLike}
				onComment={interactions.handleCommentsOpen}
				onTogglePlayback={interactions.handleTogglePlayback}
				onToggleMuted={interactions.handleToggleMuted}
				onPlaybackBlocked={interactions.handlePlaybackBlocked}
				onPlaybackTimeChange={interactions.handlePlaybackTimeChange}
				onRetryMedia={interactions.handleRetryMedia}
				onMediaLoadingChange={interactions.handleMediaLoadingChange}
			/>
			<StoryCommentsDrawer
				canAddComment={activeStory.status !== StoryVisibility.EXPIRED}
				opened={interactions.commentsOpened}
				storyId={activeStory.id}
				onClose={interactions.handleCommentsClose}
				onCommentAdded={interactions.handleCommentAdded}
				onCommentDeleted={interactions.handleCommentDeleted}
				onCommentReplyAdded={interactions.handleCommentReplyAdded}
			/>
			<AnimatePresence>
				{active && interactions.isMediaLoading ? (
					<motion.div
						className={styles.loader}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<PageLoading inline aria-label="正在加载日记" />
					</motion.div>
				) : null}
			</AnimatePresence>
		</>
	)
}
